/** @jsxImportSource @emotion/react */
import { Alert, Buttons, Spinner } from '@zip/business-components';
import { BasicHeader } from 'components';
import {
  useAuth0,
  useCookies,
  useMerchantData,
  useMerchantApplicationContext,
  useSnackbar,
} from 'contexts';
import { PageAction, PageRoute, PermissionEnum } from 'enums';
import { Constants, css } from 'global';
import { isProduction } from 'global/constants';
import DashboardLayout from 'global/dashboard-layout';
import { GenericErrorModule, HelpCentreModule } from 'modules';
import * as illustrations from 'public/illustrations';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { hasAuth0Error, logError, useMerchantApplication } from 'utils';
import * as styles from './AuthenticatedLayout.styles';
import { DashboardPage } from 'pages/activation/DashboardPage';
import { MerchantApplicationStatus } from 'types/responses/activation';

export const AuthenticatedLayout = (): JSX.Element => {
  const { loading, accessToken, isAuthenticated, loginWithRedirect, logout } =
    useAuth0();
  const { clearAuthentication } = useCookies();
  const Snackbar = useSnackbar();

  const {
    getMerchantProfile,
    getProfileData,
    profileDataIsLoading,
    profileData,
    hasPublicId,
    isUnlinkedUser,
    isLinkingUser,
    needsToReauth,
  } = useMerchantData();

  const { checkTokenPermission } = useMerchantApplication();
  const {
    activationDataLoading,
    applicationStatus,
    isPostSubmission,
    getActivationData: getActivationData,
  } = useMerchantApplicationContext();
  const postSubmission = isPostSubmission();

  // ## Content conditions ##
  const passwordHasExpired = hasAuth0Error(Constants.errors.passwordExpired);
  const suspiciousLogin = hasAuth0Error(Constants.errors.suspiciousLogin);
  const emailNotVerified = hasAuth0Error(Constants.errors.emailNotVerifiedUser);
  const applicationEmailNotVerified = hasAuth0Error(
    Constants.errors.applicationEmailNotVerified
  );

  const hasAnyAuth0Error =
    passwordHasExpired ||
    suspiciousLogin ||
    emailNotVerified ||
    applicationEmailNotVerified;

  const isMissingAuth =
    !loading &&
    !isAuthenticated &&
    !profileDataIsLoading &&
    !hasAnyAuth0Error &&
    !isUnlinkedUser() &&
    !isLinkingUser();

  const isMissingUserProfile =
    !loading &&
    !profileDataIsLoading &&
    (isUnlinkedUser() || isLinkingUser() || needsToReauth());

  const shouldShowActivation =
    !loading &&
    isAuthenticated &&
    accessToken &&
    !hasAnyAuth0Error &&
    checkTokenPermission(PermissionEnum.MerchantApplicationRead);
  const shouldShowDashboardLoading =
    (loading || profileDataIsLoading) && !shouldShowActivation;

  const hasAccessToActivationDashboard =
    applicationStatus === MerchantApplicationStatus.UserCreated ||
    postSubmission === true;

  /*
   * `profileData.permissions` is required to display certain routes
   *   and therefore required for deeplinking
   */
  const shouldShowContent =
    !loading &&
    isAuthenticated &&
    !profileDataIsLoading &&
    profileData?.permissions != null &&
    ((!isProduction && hasPublicId) || profileData) &&
    !hasAnyAuth0Error &&
    !isUnlinkedUser() &&
    !isLinkingUser() &&
    !needsToReauth();
  const shouldRenderActivationBranch =
    shouldShowActivation && !shouldShowContent;
  const activationDataIsLoading =
    shouldRenderActivationBranch && activationDataLoading;
  const shouldShowActivationError =
    shouldRenderActivationBranch &&
    !activationDataIsLoading &&
    !hasAccessToActivationDashboard;
  const shouldShowActivationDashboard =
    shouldRenderActivationBranch &&
    !activationDataIsLoading &&
    hasAccessToActivationDashboard;

  /*
   * ## Initial load ##
   *
   * On component load this section will trigger a wait for Auth0 to
   *   authenticate and load, once completed it will then call Zip APIs
   *   for the required account data via initialLoad()
   *
   * If after 3 seconds (3000ms) the component has not been unloaded it
   *   will trigger setShowCacheHelper to the value of `loading`
   */
  const [showCacheHelper, setShowCacheHelper] = useState<boolean>(false);

  // ## Help centre ##
  const [isHelpCentreOpen, setIsHelpCentreOpen] = useState<boolean>(false);

  const openHelpCentre = (): void => setIsHelpCentreOpen(true);

  async function initialLoad(): Promise<void> {
    try {
      if (shouldShowActivation) {
        await getActivationData();
      } else {
        await getMerchantProfile();
        await getProfileData();
      }

      setShowCacheHelper(false);
    } catch (err) {
      logError(PageAction.initialLoad, err);
      Snackbar.error('An error has occurred. Please try again.');
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => setShowCacheHelper(loading), 3000);

    if (!loading && isAuthenticated && accessToken) {
      initialLoad();
    }

    return (): void => clearTimeout(timer);
  }, [loading, isAuthenticated, accessToken]);

  useEffect(() => {
    if (isMissingAuth) {
      loginWithRedirect({});
    }
  }, [isMissingAuth]);

  return (
    <>
      {!shouldShowContent && !shouldShowActivation && (
        <BasicHeader
          alignLogoDesktop={hasAnyAuth0Error ? 'center' : 'space-between'}
          isAuthenticated={isAuthenticated}
          helpButton="None"
          openHelpCentre={openHelpCentre}
          bottomBorder={true}
          shouldShowActivation={shouldShowActivation}
        />
      )}

      {shouldShowDashboardLoading && (
        <div css={styles.loading} data-testid="layout-loading">
          <Spinner />

          {showCacheHelper && (
            <Alert variant="info" css={styles.cacheHelper}>
              Taking a while?{' '}
              <Buttons.Text
                onClick={(): void => clearAuthentication()}
                className="body2"
                css={styles.textButton}
              >
                Click here
              </Buttons.Text>{' '}
              to retry
            </Alert>
          )}
        </div>
      )}

      {activationDataIsLoading && (
        <div css={styles.loading} data-testid="layout-loading">
          <Spinner />
        </div>
      )}

      {isMissingUserProfile && isUnlinkedUser() && (
        <div id="content" css={styles.main}>
          <div css={styles.fullPageComm}>
            <div className="title2">Unregistered user</div>
            <p className="heading4">
              Your user account has not been set up. Please contact your account
              administrator.
            </p>
            <Buttons.Primary onClick={logout}>Log out</Buttons.Primary>
          </div>
        </div>
      )}

      {isMissingUserProfile && !isUnlinkedUser() && (
        <div id="content" css={styles.main}>
          <div css={styles.fullPageComm}>
            <div className="title2">Almost ready</div>
            <p className="heading4">
              You're set up but not quite ready to go, we just need you to
              re-authenticate to finalise your account set up.
            </p>
            <Buttons.Primary onClick={logout}>Let's go</Buttons.Primary>
          </div>
        </div>
      )}

      {!loading && passwordHasExpired && (
        <div id="content" css={styles.main}>
          <div css={styles.fullPageComm}>
            <Alert variant="error" css={styles.loginErrorAlert}>
              <p className="body2" css={css.noTopMrgn}>
                You haven't used Zip in a while. For security purposes, we've
                sent you an email requesting password reset.
              </p>
              <Buttons.Text
                onClick={(): void => logout()}
                className="body2"
                css={{ float: 'right' }}
              >
                Back to login
              </Buttons.Text>
            </Alert>
          </div>
        </div>
      )}

      {!loading && emailNotVerified && (
        <div id="content" css={styles.main}>
          <div css={styles.fullPageComm}>
            <Alert variant="error" css={styles.loginErrorAlert}>
              <p className="body2" css={css.noTopMrgn}>
                Your email needs to be verified in order to use 2-step
                verification. Please set up your password again by requesting
                reset password before logging in again.
              </p>
              <Buttons.Text
                onClick={(): void => logout()}
                className="body2"
                css={{ float: 'right' }}
              >
                Back to login
              </Buttons.Text>
            </Alert>
          </div>
        </div>
      )}

      {!loading && suspiciousLogin && (
        <div id="content" css={styles.main}>
          <div css={styles.fullPageComm}>
            <Alert variant="error" css={styles.loginErrorAlert}>
              <p className="body2" css={css.noTopMrgn}>
                We have detected suspicious login behavior and further attempts
                will be blocked. Please contact the administrator.
              </p>
              <Buttons.Text
                onClick={(): void => logout()}
                className="body2"
                css={{ float: 'right' }}
              >
                Back to login
              </Buttons.Text>
            </Alert>
          </div>
        </div>
      )}

      {!loading && applicationEmailNotVerified && (
        <GenericErrorModule
          heading="Verify your account"
          contentLines={[
            'Before you can login to our merchant dashboard you will need to verify your account.',
            'We have sent a verification email. Please click on the link to verify and continue to login.',
          ]}
          icon={illustrations.Warning}
        />
      )}

      {shouldShowActivationError && (
        <GenericErrorModule
          heading="Something went wrong"
          contentLines={[
            "We couldn't load or verify your application status.",
            'Please refresh the page or try again later.',
          ]}
          icon={illustrations.Warning}
        />
      )}

      {shouldShowActivationDashboard && (
        <Routes>
          <Route
            path={PageRoute.Application}
            element={<DashboardPage openHelpCentre={openHelpCentre} />}
          />
          <Route
            path="*"
            element={<Navigate to={PageRoute.Application} replace />}
          />
        </Routes>
      )}

      {shouldShowContent && !shouldShowActivation && (
        <>
          <Routes>
            <Route
              path="*"
              element={<DashboardLayout openHelpCentre={openHelpCentre} />}
            ></Route>
          </Routes>
        </>
      )}

      <HelpCentreModule
        isOpen={isHelpCentreOpen}
        setIsOpen={setIsHelpCentreOpen}
      />
    </>
  );
};

export default AuthenticatedLayout;
